import { assign, createMachine, send } from 'xstate';
import { escalate } from 'xstate/lib/actions';

type PollingContext<T> = {
    tries: number;
    value?: T;
};

type PollingEvents = { type: 'POLL' };
export type PollStrategy = {
    interval: number;
    retries: number;
    type: 'fixed' | 'linear-backoff' | 'exponential-backoff' | 'random-backoff';
};

export function createIntervalCalculator(strategy: PollStrategy): (tries: number) => number {
    switch (strategy.type) {
        case 'fixed':
            return () => strategy.interval;
        case 'exponential-backoff':
            return (tries: number) => Math.pow(2, tries) * strategy.interval;
        case 'linear-backoff':
            return (tries: number) => tries * strategy.interval;
        case 'random-backoff':
            return () => (Math.random() + 1) * strategy.interval;
        default:
            throw new Error(`Strategy not implemented: '${strategy.type}'.`);
    }
}

/**
 * @param service Service to poll for value.
 */
export const createPollMachine = <T>(service: () => Promise<T>, validate: (value?: T) => boolean, strategy: PollStrategy) => {
    const getInterval = createIntervalCalculator(strategy);

    /** @xstate-layout N4IgpgJg5mDOIC5QAcD2AbdBaAtgQwGMALASwDswA6NTcqAYglQsvIDdUBrKm7fY8jwzo6CdqgJ4ALiWYBtAAwBdRUsQpUsEjObqQAD0QAmACwBOSiYCsAZgCMC8wDYnADldnHAGhABPRADsrpZmoWZOdjYKrk42TlYAvgk+vLiEpCy8dIzMVOLc1MJpApnCouKSOmSqcnZqSCBoWlV6hgimFtb2juFuHt5+xkYKlHEBdhETdnZWHkZJKUX8GUK0ZAxgAE6bqJvU6NIAZrs4hZjFK2ci62JkHJWy1cqqek3aj63G5pa2Ds59nhMPn8CDsASMlBi4wC4QCNnsRlc82SjSW6UElDYWxIh189BeDTeLQabScRhslFiRiCNk8VhMJnBwMQYJMPwUVjsrnMVnikUSKNSywxWM2OLxtXqGmaHxJiDJFKpNLpDKZg3aJkVYKM0wCDK5NgWqPOwpYsAIkAArugwPQAAoAeQAMk6CdL3ro5QgFZSbNTXLSOaqjMyEPCLBFxpy6dFkSiyKgIHBXmiSmAUzLPaA2lgnKGsFZKGFiyXQobBanLll1hmPWRPggTCH1aFKNEbFZwtETGCzHYzEaheiWKLxbXidnEFYFBSPNYFB0ZtYgequSNdWD4q4YbZXIPKxjzVabePZZOEAGLDC3CZogoHI5myDyRD59PweFpwoAgF9ybh1QmxwBgWIQKeWYGIgJjbpSvJGPBJixBEmqhq2bhxIiUTwf2MR-nwAGUIceAkDaYGEpodYNpelDXtyd4Pk2oZgnY7JfsMiE2AEZJJEkQA */
    return createMachine<PollingContext<T>, PollingEvents>(
        {
            id: 'poll-machine',
            initial: 'polling',
            context: { tries: 0 },
            states: {
                polling: {
                    exit: 'increment',
                    invoke: {
                        src: 'poll',
                        onDone: [
                            {
                                cond: (_, evt) => validate(evt.data),
                                actions: assign({ value: (_, e) => e.data }),
                                target: 'resolved',
                            },
                            { target: 'verify' },
                        ],
                        onError: {
                            target: 'verify',
                        },
                    },
                },

                verify: {
                    always: [
                        {
                            cond: 'isDeparted',
                            target: 'failed',
                        },
                        { target: 'scedule' },
                    ],
                },

                scedule: {
                    entry: send('POLL', {
                        delay: (ctx) => getInterval(ctx.tries),
                    }),
                    on: {
                        POLL: 'polling',
                    },
                },

                resolved: {
                    type: 'final',
                    data: (ctx) => ctx.value,
                },

                failed: {
                    entry: escalate((ctx) => ({
                        message: `Failed to poll resource. Polled ${ctx.tries} times`,
                    })),
                    type: 'final',
                },
            },
        },
        {
            guards: {
                isDeparted: (ctx) => ctx.tries >= strategy.retries,
            },
            actions: {
                increment: assign({
                    tries: (ctx) => ctx.tries + 1,
                }),
            },
            services: { poll: () => service },
        }
    );
};
export default createPollMachine;
